import { getCard, type GroupId } from '@lafayette/content';
import type { ActionTargets, GameProjection, PlayActionOption } from '@lafayette/protocol';

import { everyone, nameOf, titleOf } from './selectors.js';

export interface ActionDraft {
  option: PlayActionOption;
  targets: ActionTargets;
  doubles: string[];
}

export type TargetStepKind = 'opponentProperty' | 'ownProperty' | 'opponentMall';

export interface TargetStep {
  kind: TargetStepKind;
  /** What the controls panel asks the player to do. */
  prompt: string;
  /** The button on the card's detail view. */
  confirmLabel: string;
  /** Instances the engine would accept. */
  legalIds: string[];
}

/** Per-card wording, so the prompt reads like the card rather than the code. */
const WORDING: Record<
  string,
  Partial<Record<TargetStepKind, { prompt: string; confirm: string }>>
> = {
  'unsolicited-offer': {
    opponentProperty: {
      prompt: "Select the card on an opponent's table to make an offer on.",
      confirm: 'Offer on this card',
    },
  },
  '1031-exchange': {
    opponentProperty: {
      prompt: "Select the card on an opponent's table you want.",
      confirm: 'Trade for this card',
    },
    ownProperty: {
      prompt: 'Now select one of your own cards to give up.',
      confirm: 'Give up this card',
    },
  },
  'mortgage-default': {
    opponentMall: {
      prompt: "Select any card in the complete strip mall you're taking.",
      confirm: 'Take this strip mall',
    },
  },
};

const FALLBACK: Record<TargetStepKind, { prompt: string; confirm: string }> = {
  opponentProperty: {
    prompt: "Select a card on an opponent's table.",
    confirm: 'Choose this card',
  },
  ownProperty: { prompt: 'Select one of your own cards.', confirm: 'Choose this card' },
  opponentMall: {
    prompt: "Select any card in an opponent's complete strip mall.",
    confirm: 'Choose this strip mall',
  },
};

function wording(definitionId: string, kind: TargetStepKind) {
  return WORDING[definitionId]?.[kind] ?? FALLBACK[kind];
}

/** Every card sitting in an opponent's complete mall the action may take. */
function mallTargets(projection: GameProjection, option: PlayActionOption): string[] {
  const ids: string[] = [];
  for (const player of projection.opponents) {
    if (!option.playerIds.includes(player.id)) continue;
    for (const group of player.completeMalls) {
      if (!option.groups.includes(group)) continue;
      ids.push(...(player.malls[group]?.cards ?? []));
    }
  }
  return ids;
}

/**
 * The card the player still has to point at, if any. Player and color choices
 * stay as buttons in the panel; only cards are picked off the table.
 */
export function targetStepFor(
  projection: GameProjection,
  draft: ActionDraft | null,
): TargetStep | null {
  if (!draft) return null;
  const { option, targets } = draft;

  const step = (kind: TargetStepKind, legalIds: string[]): TargetStep => {
    const words = wording(option.definitionId, kind);
    return { kind, prompt: words.prompt, confirmLabel: words.confirm, legalIds };
  };

  if (
    (option.targetKind === 'property' || option.targetKind === 'propertySwap') &&
    !targets.propertyInstanceId
  ) {
    return step('opponentProperty', option.propertyInstanceIds);
  }
  if (option.targetKind === 'propertySwap' && !targets.ownPropertyInstanceId) {
    return step('ownProperty', option.ownPropertyInstanceIds);
  }
  if (option.targetKind === 'completeMall' && option.playerIds.length > 0 && !targets.group) {
    return step('opponentMall', mallTargets(projection, option));
  }
  return null;
}

/** Where a card instance lives, across every seat. */
export function locateInstance(
  projection: GameProjection,
  instanceId: string,
): { playerId: string; group: GroupId; complete: boolean } | null {
  for (const player of everyone(projection)) {
    for (const [group, mall] of Object.entries(player.malls)) {
      if (mall?.cards.includes(instanceId)) {
        return {
          playerId: player.id,
          group: group as GroupId,
          complete: player.completeMalls.includes(group as GroupId),
        };
      }
    }
  }
  return null;
}

/**
 * Why this card cannot be used for the step in progress. Returned in the card's
 * own detail view so a rejected pick explains itself rather than doing nothing.
 */
export function explainIneligible(
  projection: GameProjection,
  step: TargetStep,
  instanceId: string,
  definitionId: string,
): string {
  const cardName = getCard(definitionId).title;
  const where = locateInstance(projection, instanceId);

  if (!where) {
    return `${cardName} needs a property that is already on the table.`;
  }

  const mine = where.playerId === projection.self.id;

  if (step.kind === 'ownProperty') {
    if (!mine)
      return `Choose one of your own properties to give up, not ${nameOf(projection, where.playerId)}'s.`;
    if (where.complete) {
      return `${titleOf(projection, instanceId)} is part of a complete strip mall, so it cannot be traded away.`;
    }
    return `${cardName} cannot use this card.`;
  }

  if (mine) {
    return `This one is yours. ${cardName} takes a card from an opponent's table.`;
  }

  if (step.kind === 'opponentMall') {
    return `${nameOf(projection, where.playerId)}'s strip mall is not complete yet, so ${cardName} cannot take it.`;
  }

  if (where.complete) {
    return `${titleOf(projection, instanceId)} is part of a complete strip mall, so ${cardName} cannot take it.`;
  }

  return `${cardName} cannot take this card.`;
}

/** Applies a chosen card to the draft, returning the updated targets. */
export function applyTarget(
  projection: GameProjection,
  step: TargetStep,
  targets: ActionTargets,
  instanceId: string,
): ActionTargets {
  if (step.kind === 'opponentProperty') return { ...targets, propertyInstanceId: instanceId };
  if (step.kind === 'ownProperty') return { ...targets, ownPropertyInstanceId: instanceId };

  const where = locateInstance(projection, instanceId);
  if (!where) return targets;
  return { ...targets, playerId: where.playerId, group: where.group };
}

/** True when nothing is left to choose and the command can be sent. */
export function isDraftReady(option: PlayActionOption, targets: ActionTargets): boolean {
  switch (option.targetKind) {
    case 'none':
      return true;
    case 'player':
      return targets.playerId !== undefined;
    case 'property':
      return targets.propertyInstanceId !== undefined;
    case 'propertySwap':
      return (
        targets.propertyInstanceId !== undefined && targets.ownPropertyInstanceId !== undefined
      );
    case 'completeMall':
      return (
        targets.group !== undefined &&
        (option.playerIds.length === 0 || targets.playerId !== undefined)
      );
    case 'rentGroup':
      return targets.group !== undefined;
    case 'rentGroupAndPlayer':
      return targets.group !== undefined && targets.playerId !== undefined;
  }
}
